import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragStartEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { CSSProperties, useCallback, useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';

import { useSnackbar } from '@cms/providers/SnackbarProvider';
import { BROWSER_EVENTS, SUBTYPE_OPTIONS } from '@cms/utils/constants';
import { generateSearchParam, renderDate, renderTime } from '@cms/utils/helpers';
import { ISystemCollection, ISystemCollectionItem, ISystemEvent, ISystemReport, ISystemStory, ISystemUser, SUBTYPES } from '@dl/declarations';
import api from '../../utils/api';
import { CollectionFormData, collectionSchema, initialValues } from '../../utils/forms/collection';
import { CollectionsPath, EventsPath, ReportsPath, Routes, StoriesPath, UsersPath } from '../../utils/routes';
import { Button } from '../data/Button';
import { ControlledSystemDialog } from '../data/Dialog';
import { ControlledDropdown } from '../data/Dropdown';
import { SystemImage } from '../data/Image';
import { SystemMenu } from '../data/Menu';
import { SystemPopover } from '../data/Popover';
import { ControlledSystemTextInput } from '../data/TextInput';
import { FileWarningIcon, PinIcon } from '../icons';
import { renderAttribute } from '../renders';
import { WrappedLoader } from '../ui/Loader';
import { FormContainer } from './FormContainer';

import { BsThreeDots } from 'react-icons/bs';
import { CiMapPin } from 'react-icons/ci';
import { HiOutlineBars2 } from 'react-icons/hi2';
import { IoTrashBin } from 'react-icons/io5';

const UpdateCollectionMenu = () => {
    return (
        <SystemPopover
            trigger={{
                component: (
                    <Button
                        inputVariant='white_text_dark_bg'
                    >
                        Update
                    </Button>
                )
            }}
            content={{
                component: (
                    <UpdateCollectionForm />
                )
            }}
        />
    )
}

const CancelMenu = () => {
    const [, setSearchParams] = useSearchParams();

    const methods = useFormContext<{ items: Array<ISystemCollectionItem & { remove?: boolean; }> }>();
    const { formState } = methods;

    const handleCancel = () => {
        setSearchParams({
            [generateSearchParam(CollectionsPath, "mode")]: 'view'
        });
    }

    if (!formState.isDirty) {
        return (
            <Button
                onClick={handleCancel}
            >
                Cancel
            </Button>
        )
    }

    return (
        <ControlledSystemDialog
            dialogProps={{
                title: 'Cancel',
                maxWidth: 'sm',
            }}
            trigger={{
                component: (
                    <Button
                        type='button'
                    >
                        Cancel
                    </Button>
                )
            }}
            content={{
                component: (
                    <p>
                        Do you want to cancel the changes?
                    </p>
                )
            }}
            actionsProps={{
                submit: {
                    label: 'Cancel',
                    inputVariant: 'white_text_blue_bg',
                    onClick: handleCancel
                },
                cancel: {
                    label: 'No',
                }
            }}
        />
    )
}

export const ReleaseMenu = () => {
    const queryClient = useQueryClient()
    const { showSnackbar } = useSnackbar();

    const [searchParams] = useSearchParams();
    const collectionId = searchParams.get(generateSearchParam(CollectionsPath, "id"));

    const methods = useFormContext<{ items: Array<ISystemCollectionItem & { remove?: boolean; }> }>();
    const { formState } = methods;

    const releaseCollectionStoriesMutation = useMutation({
        mutationFn: async () => {
            const items = methods.getValues('items');

            const updated_items: Array<{ item_id: string; order: number; is_pinned: boolean }> = []
            const removed_items: Array<{ item_id: string }> = []

            items.forEach((item) => {
                if (item.remove) {
                    removed_items.push({
                        item_id: item.item_ref
                    });
                }
                else {
                    updated_items.push({
                        item_id: item.item_ref,
                        order: item.order,
                        is_pinned: item.is_pinned
                    });
                }
            });

            await api.patch(`/collections/${collectionId}/items`, {
                updated_items: updated_items.map((item, index) => ({ ...item, order: index + 1 })),
                removed_items
            });
        },
        onError(error) {
            showSnackbar('Error releasing collection. ' + (error as Error).message || 'An error occurred', 'error');
        },
        onSuccess() {
            showSnackbar('Collection released successfully', 'success');

            window.dispatchEvent(new Event(BROWSER_EVENTS.CLOSE_MODAL));

            queryClient.invalidateQueries({ queryKey: ['collections-items', collectionId] });
        }
    });

    const handleRelease = () => {
        releaseCollectionStoriesMutation.mutateAsync()
    }

    return (
        <ControlledSystemDialog
            dialogProps={{
                title: 'Release',
                maxWidth: 'sm',
            }}
            trigger={{
                component: (
                    <Button
                        tooltip={formState.isDirty ? '' : 'No changes to release'}
                        inputVariant='white_text_blue_bg'
                        disabled={!formState.isDirty}
                    >
                        Release
                    </Button>
                ),
                disabled: !formState.isDirty
            }}
            content={{
                component: (
                    <p>
                        Do you want to release this collection on the website?
                    </p>
                )
            }}
            actionsProps={{
                submit: {
                    label: 'Release',
                    inputVariant: 'white_text_blue_bg',
                    onClick: handleRelease
                },
                cancel: {
                    label: 'No',
                }
            }}
        />
    )
}

const Form = () => {
    const { watch } = useFormContext<CollectionFormData>()

    const status_value = watch('status', 'released')

    return (
        <div className='mx-auto flex flex-col gap-4 min-w-[520px] px-8'>
            <ControlledDropdown
                name='subtype'
                inputSize='full'
                options={[
                    ...SUBTYPE_OPTIONS,
                    {
                        _id: SUBTYPES.SYSTEM.ID,
                        slug: SUBTYPES.SYSTEM.SLUG,
                        label: SUBTYPES.SYSTEM.NAME,
                        groupKey: "Subtypes",
                    }
                ]}
                valueKey='_id'
                description='Type'
                allowSingleDeselect={false}
                searchOptions={{
                    allow: false
                }}
                required
            />

            <ControlledDropdown
                name='collection_type'
                inputSize='full'
                options={[
                    { _id: '1', label: 'Stories', slug: 'Story', groupKey: 'Options' },
                    { _id: '2', label: 'Reports', slug: 'Report', groupKey: 'Options' },
                    { _id: '3', label: 'Events', slug: 'Event', groupKey: 'Options' },
                    { _id: '4', label: 'Users', slug: 'User', groupKey: 'Options' },
                ]}
                valueKey='slug'
                description='Used for'
                allowSingleDeselect={false}
                searchOptions={{
                    allow: false
                }}
                required
            />

            <ControlledSystemTextInput
                description='Name'
                inputSize='full'
                name='name'
                required
            />

            <ControlledSystemTextInput
                description='Description'
                inputSize='full'
                name='description'
                multiline
                rows={3}
            />

            {renderAttribute({
                title: {
                    value: 'Status'
                },
                value: {
                    value: [status_value],
                    className: 'text-cs-500'
                }
            })}
        </div>
    )
}

export const CreateCollectionForm = () => {
    return (
        <FormContainer<CollectionFormData>
            title='Create collection'
            formSize='full'
            headerOptions={{
                show: true,
                variant: 'white_text_dark_bg'
            }}
            queryOptions={{
                enabled: true,
                basePath: CollectionsPath,
            }}
            schema={collectionSchema}
            defaultValues={initialValues}
            handleCancelOverride={() => {
                window.dispatchEvent(new Event(BROWSER_EVENTS.CLOSE_MODAL));
            }}
            handlePostSubmit={() => {
                window.dispatchEvent(new Event(BROWSER_EVENTS.CLOSE_MODAL));
            }}
            loaderOptions={{
                loaderSize: 'sm'
            }}
        >
            <Form />
        </FormContainer>
    )
}

export const UpdateCollectionForm = () => {
    return (
        <FormContainer<CollectionFormData>
            title='collection'
            formSize='full'
            headerOptions={{
                show: true,
                variant: 'white_text_dark_bg'
            }}
            queryOptions={{
                enabled: true,
                basePath: CollectionsPath
            }}
            deleteOptions={{
                show: true
            }}
            schema={collectionSchema}
            defaultValues={initialValues}
            handleCancelOverride={() => {
                window.dispatchEvent(new Event(BROWSER_EVENTS.CLOSE_MODAL));
            }}
            loaderOptions={{
                loaderSize: 'sm'
            }}
        >
            <Form />
        </FormContainer>
    )
}

export const UpdateCollectionPage = () => {
    const [searchParams] = useSearchParams();
    const collectionId = searchParams.get(generateSearchParam(CollectionsPath, "id"));

    const methods = useForm<{ items: Array<ISystemCollectionItem & { remove?: boolean; }> }>({
        defaultValues: {
            items: []
        },
    });

    const { setValue } = methods;

    const { data = { collection: null, items: [] }, isLoading: isDataLoading } = useQuery({
        queryKey: ['collections-items', collectionId],
        queryFn: async () => {
            const res = await api.get<{ collection: ISystemCollection, items: Array<ISystemCollectionItem> }>(`/collections/${collectionId}/items`);
            return res.data
        },
        enabled: !!collectionId,
    })

    useEffect(() => {
        setValue('items', data.items);
    }, [data]);

    if (isDataLoading) {
        return (
            <WrappedLoader />
        )
    }

    return (
        <FormProvider {...methods}>
            <div className='py-4 flex flex-col gap-2'>
                <div className='w-full flex items-start justify-between'>
                    <div
                        className='flex flex-col gap-2 max-w-1/2'
                    >
                        {data?.collection && (
                            <>
                                <h4 className='font-semibold'>
                                    {data?.collection?.name}
                                </h4>
                                <p className=''>
                                    {data?.collection?.description}
                                </p>
                            </>
                        )}
                    </div>

                    <div className='flex items-center justify-between gap-2'>
                        <CancelMenu />
                        {data?.items?.length > 0 && (
                            <ReleaseMenu />
                        )}
                        <UpdateCollectionMenu />
                    </div>
                </div>

                <VirtualizedCollectionItemsList />
            </div>
        </FormProvider>
    )
}

export const VirtualizedCollectionItemsList = () => {
    return (
        <SortableCollectionItemsList />
    )
}

interface SortableCollectionItemsListProps { }

const SortableCollectionItemsList: React.FC<SortableCollectionItemsListProps> = () => {
    const { control } = useFormContext<{ items: Array<ISystemCollectionItem & { remove?: boolean; }> }>()
    const { fields: items, move, update } = useFieldArray({
        control,
        name: 'items'
    });
    const [activeId, setActiveId] = useState<string | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active && over && active.id !== over.id) {
            const oldIndex = items.findIndex((item) => item._id === active.id);
            const newIndex = items.findIndex((item) => item._id === over.id);
            move(oldIndex, newIndex);
        }

        setActiveId(null);
    }

    const handleClickPin = useCallback((index: number, should_pin: boolean) => {
        const item = items[index];

        update(index, {
            ...item,
            is_pinned: should_pin
        });

        if (should_pin) {
            move(index, 0);
        }
    }, [items]);

    // Moves one up in the list
    const handleMoveUp = useCallback((index: number) => {
        move(index, index - 1);
    }, [items])

    // Moves one down in the list
    const handleMoveDown = useCallback((index: number) => {
        move(index, index + 1);
    }, [items])

    const handleRemoveItem = useCallback((index: number, remove: boolean) => {
        // remove(index);

        const item = items[index];

        update(index, {
            ...item,
            remove
        });
    }, [items]);

    const renderItem = useCallback((item: ISystemCollectionItem) => {
        switch (item.item_ref_type) {
            case "Story": {
                const usedItem = item as ISystemCollectionItem<string, ISystemStory>;
                const storyObject = usedItem?.itemRefObject;
                const savedStoryVersionObject = usedItem?.itemRefObject?.savedStoryVersionObject;

                return (
                    <>
                        <SystemImage
                            src={savedStoryVersionObject?.mainAssetObject?.src_url}
                            alt={savedStoryVersionObject?.headline}
                            className='col-span-1 object-cover object-top'
                        />

                        <Link
                            to={{
                                pathname: Routes.EDITOR,
                                search: generateSearchParam(StoriesPath, "id", usedItem?.item_ref)
                            }}
                            target='_blank'
                            className='col-span-3 border-none text-base text-cs-600 text-wrap'
                        >
                            {savedStoryVersionObject?.headline}
                        </Link>

                        {renderAttribute({
                            title: {
                                value: 'Status'
                            },
                            value: {
                                value: [storyObject?.workflow || ''],
                                className: 'text-cs-500'
                            },
                        }, {
                            className: 'flex-col items-start col-span-2'
                        })}

                        {renderAttribute({
                            title: {
                                value: 'Author'
                            },
                            value: {
                                value: (savedStoryVersionObject?.authorsObjects || []).map((author) => author.name)
                            }
                        }, {
                            className: 'flex-col items-start col-span-2'
                        })}

                        <div className='flex flex-col justify-center gap-2 col-span-2'>
                            <div>
                                First published on
                            </div>
                            <div>
                                {renderDate(storyObject?.first_publish_date || '')}
                            </div>
                            <div>
                                {renderTime(storyObject?.first_publish_date || '')}
                            </div>
                        </div>
                    </>
                )
            }
            case "Report": {
                const usedItem = item as ISystemCollectionItem<string, ISystemReport>;
                return (
                    <>
                        <SystemImage
                            src={usedItem?.itemRefObject?.reportImageObject?.src_url}
                            alt={usedItem?.itemRefObject?.name}
                            className='col-span-1 object-cover object-top'
                        />

                        <div className='col-span-9'>
                            <Button
                                as='link'
                                to={{
                                    pathname: Routes.REPORTS,
                                    search: `?${generateSearchParam(ReportsPath, "id", usedItem?.item_ref)}&${generateSearchParam(ReportsPath, "mode", "update")}`
                                }}
                                target='_blank'

                                inputVariant='none'
                                className='border-none text-base text-cs-600 text-wrap'
                            >
                                {usedItem?.itemRefObject?.name}
                            </Button>
                        </div>
                    </>
                )
            }
            case "Event": {
                const usedItem = item as ISystemCollectionItem<string, ISystemEvent>;
                return (
                    <>
                        <SystemImage
                            src={usedItem?.itemRefObject?.imageObject?.src_url}
                            alt={usedItem?.itemRefObject?.name}
                            className='col-span-1 object-cover object-top'
                        />

                        <div className='col-span-9'>
                            <Button
                                as='link'
                                to={{
                                    pathname: Routes.EVENTS,
                                    search: `${generateSearchParam(EventsPath, "id", usedItem?.item_ref)}&${generateSearchParam(EventsPath, "mode", "update")}`
                                }}
                                target='_blank'

                                inputVariant='none'
                                className='border-none text-base text-cs-600 text-wrap'
                            >
                                {usedItem?.itemRefObject?.name}
                            </Button>
                        </div>
                    </>
                )
            }
            case "User": {
                const usedItem = item as ISystemCollectionItem<string, ISystemUser>;
                return (
                    <>
                        <img
                            src={usedItem?.itemRefObject?.imageObject?.src_url}
                            alt={usedItem?.itemRefObject?.name}
                            className='col-span-1 object-cover object-top'
                        />

                        <div className='col-span-9'>
                            <Button
                                as='link'
                                to={{
                                    pathname: Routes.USERS,
                                    search: `${generateSearchParam(UsersPath, "id", usedItem?.item_ref)}&${generateSearchParam(UsersPath, "mode", "update")}`
                                }}
                                target='_blank'

                                inputVariant='none'
                                className='border-none text-base text-cs-600 text-wrap'
                            >
                                {usedItem?.itemRefObject?.name}
                            </Button>
                        </div>
                    </>
                )
            }
            default:
                break;
        }
    }, [])

    const isAllowedToMove = (oldIndex: number, newIndex: number) => {
        const oldItem = items[oldIndex];
        const newItem = items[newIndex];

        if (oldItem.is_pinned !== newItem.is_pinned) {
            return false;
        }

        return true;
    }

    if (items.length === 0) {
        return (
            <div className='w-full mx-auto flex flex-col items-center justify-center'>
                <FileWarningIcon />
                <h4 className="font-semibold">This collection is currently empty </h4>
                <p>Consult the Product team before adding any new items</p>
            </div>
        )
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <SortableContext
                items={items.map(item => item._id)}
                strategy={verticalListSortingStrategy}
            >
                {items.map((collectionItem, index) => (
                    // grid-cols-12
                    <SortableCollectionItem
                        id={collectionItem._id}
                        key={collectionItem._id}
                        isDisabled={collectionItem.is_pinned}
                        isDragging={activeId === collectionItem._id}
                        wilRemove={collectionItem.remove}
                    >
                        <div className='col-span-1 flex items-center gap-4'>
                            {collectionItem.remove ? (
                                <button
                                    type='button'
                                    className='cursor-pointer'
                                    onMouseDown={() => handleRemoveItem(index, false)}
                                >
                                    <IoTrashBin width={20} height={30} color='red' />
                                </button>
                            ) : (
                                <button
                                    type='button'
                                    className='cursor-pointer'
                                    onMouseDown={() => handleClickPin(index, !collectionItem.is_pinned)}
                                >
                                    {collectionItem.is_pinned ? <PinIcon width={20} height={30} /> : <CiMapPin width={20} height={30} />}
                                </button>
                            )}
                            <div>
                                {index + 1}
                            </div>
                        </div>

                        {renderItem(collectionItem)}

                        <div
                            className='col-span-1 flex items-center justify-between'
                        >
                            <SystemMenu
                                trigger={{
                                    component: (
                                        <button
                                            type='button'
                                            className='cursor-pointer'
                                        >
                                            <BsThreeDots size={32} />
                                        </button>
                                    )
                                }}
                                content={{
                                    component: (
                                        <div className='min-w-52 p-4 flex flex-col items-start gap-2'>
                                            {(
                                                index > 0
                                                && isAllowedToMove(index, index - 1)
                                                // && !items[index - 1].is_pinned
                                            ) && (
                                                    <button
                                                        type='button'
                                                        className='cursor-pointer font-medium'
                                                        onClick={() => handleMoveUp(index)}
                                                    >
                                                        Move to Top
                                                    </button>
                                                )}
                                            {(
                                                index < items.length - 1
                                                && isAllowedToMove(index, index + 1)
                                                // && !items[index + 1].is_pinned
                                            ) && (
                                                    <button
                                                        type='button'
                                                        className='cursor-pointer font-medium'
                                                        onClick={() => handleMoveDown(index)}
                                                    >
                                                        Move to Bottom
                                                    </button>
                                                )}
                                            <button
                                                type='button'
                                                className='cursor-pointer font-medium text-orange-400'
                                                onClick={() => handleRemoveItem(index, true)}
                                            >
                                                Remove from collection
                                            </button>
                                        </div>
                                    ),
                                    shouldCloseOnItemClick: true
                                }}
                            />

                            <div
                                className={clsx(
                                    'p-2 rounded-md transition-colors duration-200',
                                    // isDragging ? 'bg-cs-300' : 'bg-cs-200'
                                )}
                            >
                                <HiOutlineBars2 size={32} />
                            </div>
                        </div>
                    </SortableCollectionItem>
                ))}
            </SortableContext>
        </DndContext>
    )
}

interface SortableCollectionItemProps extends React.PropsWithChildren {
    id: string;
    isDisabled?: boolean;
    isDragging?: boolean;
    wilRemove?: boolean;
}

const SortableCollectionItem: React.FC<SortableCollectionItemProps> = (props) => {
    const { id, children, isDisabled = false, isDragging = false, wilRemove = false } = props;

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging: isSortableDragging,
    } = useSortable({
        id,
        transition: {
            duration: 250,
            easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
        },
        disabled: isDisabled
    });

    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isSortableDragging ? 0.5 : 1,
        zIndex: isSortableDragging ? 1000 : 'auto',
        cursor: isDisabled ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={clsx(
                'w-full grid grid-cols-12 items-center justify-between p-4 my-2 gap-4',
                'border  rounded-md transition-all duration-200',
                wilRemove ? 'border-red-500' : 'border-cs-700',
                {
                    'shadow-lg ring-2 ring-blue-400': isDragging,
                    'bg-gray-50': isDragging,
                    'opacity-50': isSortableDragging,
                }
            )}
            {...attributes}
            {...listeners}
        >
            {children}
        </div>
    );
}